import React, { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  ArrowRight, 
  ArrowLeft, 
  Frame, 
  Heart, 
  Users, 
  Calendar, 
  Plus, 
  X,
  AlertCircle,
  Shield,
  Ban,
  BookOpen
} from "lucide-react";
import StepWrapper from "./StepWrapper";
import { WhyItMatters } from "./HelpComponents";

const TagInput = ({ items, onAdd, onRemove, placeholder, icon: Icon }) => {
  const [inputValue, setInputValue] = useState("");

  const handleAdd = () => {
    if (inputValue.trim() && !items.includes(inputValue.trim())) {
      onAdd(inputValue.trim());
      setInputValue("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1 bg-background border-input"
        />
        <Button 
          type="button" 
          variant="outline" 
          size="icon"
          onClick={handleAdd}
          disabled={!inputValue.trim()}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {items.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {items.map((item, index) => (
            <Badge 
              key={index} 
              variant="secondary"
              className="px-3 py-1.5 text-sm bg-secondary text-secondary-foreground"
            >
              {Icon && <Icon className="w-3 h-3 mr-1.5" />}
              {item}
              <button
                onClick={() => onRemove(index)}
                className="ml-2 hover:text-destructive transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

const StepFrameValues = ({ data, updateData, onNext, onBack, coachMode = true }) => {
  const addToArray = (field, value) => {
    updateData(field, [...data[field], value]);
  };

  const removeFromArray = (field, index) => {
    updateData(field, data[field].filter((_, i) => i !== index));
  };

  return (
    <StepWrapper
      stepNumber={2}
      title="Frame & Values"
      subtitle={coachMode ? "Clarify the decision you're facing and understand what matters most to you." : null}
    >
      <div className="space-y-6">
        {/* Introduction Context - only in coach mode */}
        {coachMode && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          className="bg-primary/5 border border-primary/20 rounded-lg p-4"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-sm mb-1">Building Your Decision Chain</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                <strong>Frame</strong> defines what you&apos;re deciding, who&apos;s involved, and any time constraints. 
                <strong> Values</strong> clarify what you want to achieve and what you want to avoid. 
                Together, they ensure you&apos;re solving the right problem with the right priorities.
              </p>
            </div>
          </div>
        </motion.div>
        )}

        {/* Frame Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: coachMode ? 0.2 : 0.1 }}
        >
          <Card className="p-6 bg-card border-border shadow-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-chain-frame/20 flex items-center justify-center">
                <Frame className="w-5 h-5 text-chain-frame" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Frame the Situation</h2>
                {coachMode && <p className="text-sm text-muted-foreground">Define the decision and context</p>}
              </div>
            </div>

            {coachMode && (
              <WhyItMatters>
                A helpful frame clarifies exactly what you&apos;re deciding. Often, we feel stuck because 
                we&apos;re trying to solve too many things at once or haven&apos;t clearly defined the problem. 
                Getting the frame right is like aiming before you shoot.
              </WhyItMatters>
            )}

            <div className={`space-y-6 ${coachMode ? 'mt-4' : 'mt-2'}`}>
              {/* Decisions I face */}
              <div>
                <Label className="text-sm font-medium text-foreground mb-1 block">
                  What decisions do I face?
                </Label>
                {coachMode && (
                  <p className="text-xs text-muted-foreground mb-2">
                    Break down your situation into specific decisions. &quot;Should I change schools?&quot; is clearer than &quot;What should I do about school?&quot;
                  </p>
                )}
                <TagInput
                  items={data.decisionsIFace}
                  onAdd={(value) => addToArray("decisionsIFace", value)}
                  onRemove={(index) => removeFromArray("decisionsIFace", index)}
                  placeholder="Type a decision and press Enter..."
                  icon={AlertCircle}
                />
              </div>

              {/* Who else is involved */}
              <div>
                <Label className="text-sm font-medium text-foreground mb-1 block">
                  Who else is involved?
                </Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Consider who will be affected by or has influence over this decision.
                </p>
                <TagInput
                  items={data.whoElseInvolved}
                  onAdd={(value) => addToArray("whoElseInvolved", value)}
                  onRemove={(index) => removeFromArray("whoElseInvolved", index)}
                  placeholder="Add people involved..."
                  icon={Users}
                />
              </div>

              {/* Deadline */}
              <div>
                <Label htmlFor="deadline" className="text-sm font-medium text-foreground mb-2 block">
                  <Calendar className="w-4 h-4 inline mr-2" />
                  Is there a deadline?
                </Label>
                <Input
                  id="deadline"
                  value={data.deadline}
                  onChange={(e) => updateData("deadline", e.target.value)}
                  placeholder="e.g., By next month, Before graduation..."
                  className="bg-background border-input"
                />
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Values Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Card className="p-6 bg-card border-border shadow-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-chain-values/20 flex items-center justify-center">
                <Heart className="w-5 h-5 text-chain-values" />
              </div>
              <div>
                <h2 className="font-semibold text-foreground">Clarify Your Values</h2>
                <p className="text-sm text-muted-foreground">What matters most in this decision?</p>
              </div>
            </div>

            <WhyItMatters>
              Values are your compass. They help you evaluate which option is truly best for you. 
              Understanding what you want AND what you want to avoid prevents regret and helps 
              identify trade-offs you might need to make.
            </WhyItMatters>

            <div className="space-y-6 mt-4">
              {/* What is at stake */}
              <div>
                <Label className="text-sm font-medium text-foreground mb-1 block">
                  <Shield className="w-4 h-4 inline mr-2" />
                  What is at stake?
                </Label>
                <p className="text-xs text-muted-foreground mb-2">
                  What could you gain or lose? What are the potential consequences?
                </p>
                <TagInput
                  items={data.whatIsAtStake}
                  onAdd={(value) => addToArray("whatIsAtStake", value)}
                  onRemove={(index) => removeFromArray("whatIsAtStake", index)}
                  placeholder="What could you gain or lose..."
                />
              </div>

              {/* What I want */}
              <div>
                <Label className="text-sm font-medium text-foreground mb-2 block">
                  <Heart className="w-4 h-4 inline mr-2" />
                  What do I want?
                </Label>
                <TagInput
                  items={data.whatIWant}
                  onAdd={(value) => addToArray("whatIWant", value)}
                  onRemove={(index) => removeFromArray("whatIWant", index)}
                  placeholder="Add what you want from this decision..."
                />
              </div>

              {/* What I want to avoid */}
              <div>
                <Label className="text-sm font-medium text-foreground mb-2 block">
                  <Ban className="w-4 h-4 inline mr-2" />
                  What do I want to avoid?
                </Label>
                <TagInput
                  items={data.whatIWantToAvoid}
                  onAdd={(value) => addToArray("whatIWantToAvoid", value)}
                  onRemove={(index) => removeFromArray("whatIWantToAvoid", index)}
                  placeholder="Add what you want to avoid..."
                />
              </div>

              {/* Tradeoffs */}
              <div>
                <Label htmlFor="tradeoffs" className="text-sm font-medium text-foreground mb-2 block">
                  Any trade-offs between these values?
                </Label>
                <Textarea
                  id="tradeoffs"
                  value={data.tradeoffs}
                  onChange={(e) => updateData("tradeoffs", e.target.value)}
                  placeholder="Describe any conflicts between what you want..."
                  className="min-h-[80px] resize-none bg-background border-input"
                />
              </div>
            </div>
          </Card>
        </motion.div>

        {/* Navigation */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-between items-center pt-4"
        >
          <Button
            variant="ghost"
            onClick={onBack}
            className="text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <Button
            onClick={onNext}
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-6"
          >
            Continue
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </motion.div>
      </div>
    </StepWrapper>
  );
};

export default StepFrameValues;
